import { BASE_URL, DEFAULT_MAX_PRECISION } from "../../../global";
import axiosWithToken from "../../../utils/components/axiosTokenConfig";
import Decimal from "decimal.js";
import { containsCustomUnitPair, customUnitConversion } from "../../../utils/components/unitConversion/customUnitConvertor";
import unitConvertor, { getBaseUnit, isCustomUnit } from "../../../utils/components/unitConversion/unitConvertor";

const EXTRA_PRECISION_SPLITTER = " + ", DEFAULT_PRECISION = 2, COLUMN_READ_OFFSET = 1, MAX_ITER = 20;
const BASE_CONVERT_UNITS = ["s", "°"]; // base units that trigger unitConvertor() on default formulas
const MATERIAL_TYPE_CONVERTOR_RE = /unitConvertor\s*\(\s*(c\d+)\s*,\s*type\s*:\s*standard\.materialType\s*\)/gi;
const COL_REF_RE = /\bcol(\d+)\b/gi;
const DATASHEET_REF_RE = /\$datasheet\.([A-Za-z_]\w*)/gi;
const STEP_REF_RE = /\$step(\d+)\b/gi;
// "$" is now optional: both "$standardRanges.cmv" and "standardRanges.cmv" (and ".all" / scope) resolve.
// (?<![\w.]) stops it from matching inside another word or path, e.g. "xstandardRanges.cmv" or "a.standardRanges.cmv".
const STANDARD_RANGES_REF_RE = /(?<![\w.$])\$?standardRanges\.([A-Za-z_]\w*)(?:\.(\w+))?\b/gi;
const BARE_WORD_RE = /\b[A-Za-z_]\w*\b/g;
// Used by the general SQL engine to find placeholders; also picks up bare "standardRanges.x[.scope]".
const SQL_PLACEHOLDER_RE = /\$[A-Za-z_][\w.]*|(?<![\w.$])standardRanges\.[A-Za-z_]\w*(?:\.\w+)?/gi;
const SQL_ONLY_RE = /\bover\s*\(|\bfrom\s*\(|\bcast\s*\(|'/i; // shapes the fixed step regexes can never evaluate

// NOTE: functions marked SYNC are called from native sync callbacks (String.replace, Array.filter/map)
// or inside the sandboxed `new Function(...)` evaluator — they must never become async.

/* ---------------------------- basic helpers ---------------------------- */

const lastCIndex = (row) => Math.max(0, ...Object.keys(row).map((k) => /^c(\d+)$/.exec(k)).filter(Boolean).map((m) => +m[1])); // SYNC
// SYNC — "colN" reads c(N-1); one past the last real c-column reads `uncertainty`.
function readCol(row, n) {
  const idx = n - COLUMN_READ_OFFSET, key = `c${idx}`;
  return key in row ? row[key] : idx === lastCIndex(row) + 1 ? row.uncertainty : undefined;
}
const colSource = (row, n) => { // SYNC — which property "colN" really reads (error messages)
  const idx = n - COLUMN_READ_OFFSET;
  return `c${idx}` in row ? `c${idx}` : idx === lastCIndex(row) + 1 ? "uncertainty" : `c${idx} (missing)`;
};
const toPlain = (n) => new Decimal(n).toFixed(); // SYNC — plain decimal string, no exponent form
const cleanFloat = (n) => Number(Number(n).toPrecision(15)); // SYNC — strips float noise (digits 16-17)
function roundWithDecimal(value, precision = 0) { // SYNC — precision-safe half-up rounding
  if (value === null || value === undefined || value === "" || isNaN(value)) return value;
  const dp = Number.isInteger(precision) && precision >= 0 ? precision : DEFAULT_PRECISION;
  return new Decimal(cleanFloat(value)).toDecimalPlaces(dp, Decimal.ROUND_HALF_UP).toFixed(dp);
}
const cleanNumeric = (val) => (typeof val === "string" ? val.replace(/[$~]/g, "") : val); // SYNC — strips "$" and "~"
const numVal = (val) => parseFloat(cleanNumeric(val));
const isResolved = (val) => !(val === undefined || val === null || val === "$" || val === "") && !isNaN(parseFloat(cleanNumeric(val))); // SYNC
const hasValue = (val) => val !== undefined && val !== null && val !== "$" && val !== ""; // SYNC — any value, even non-numeric
// SYNC — old engine stored step numerics as DOUBLE(18, DEFAULT_MAX_PRECISION); step reads must match.
const stepNumVal = (val) => { const n = numVal(val); return Number.isFinite(n) ? Number(roundWithDecimal(n, DEFAULT_MAX_PRECISION)) : n; };
const isDataRow = (row) => typeof row.c1 !== "string" || (!row.c1.includes("_unit_") && !row.c1.includes("_rh_")); // SYNC
const findUnitRow = (rows) => rows?.find((r) => typeof r.c1 === "string" && r.c1.includes("_unit_")) || null; // SYNC
const stripUnit = (u) => (typeof u === "string" && u.replace("_unit_", "")) || null; // SYNC
const collectColRefs = (expr) => [...new Set([...expr.matchAll(COL_REF_RE)].map((m) => parseInt(m[1], 10)))]; // SYNC
const subCols = (expr, row) => expr.replace(COL_REF_RE, (_, n) => `(${toPlain(stepNumVal(readCol(row, +n)))})`); // SYNC
const groupBy = (rows, keyFn, valFn = (r) => r) => { // SYNC
  const m = new Map();
  rows.forEach((r) => { const k = keyFn(r); if (!m.has(k)) m.set(k, []); m.get(k).push(valFn(r)); });
  return m;
};

function applyMround(num) { // SYNC — round integer part to nearest 10
  const [intPart, decPart] = String(num).split(".");
  const rounded = Math.round(Number(intPart) / 10) * 10;
  return Number(decPart !== undefined ? `${rounded}.${decPart}` : rounded);
}
function roundStoredValue(rawValue, customPrecisionRaw, precision) { // SYNC — formats a manual cell; null if unchanged
  if (rawValue === undefined || rawValue === null) return null;
  const cleaned = cleanNumeric(String(rawValue));
  if (cleaned === "" || isNaN(cleaned)) return cleaned !== rawValue ? cleaned : null;
  const kind = String(customPrecisionRaw).split(".")[0].split(EXTRA_PRECISION_SPLITTER)[0];
  let num = Number(cleaned);
  if (kind !== "7" && String(customPrecisionRaw).includes("mround")) num = applyMround(num);
  const newVal = roundWithDecimal(num, precision);
  return newVal !== rawValue ? newVal : null;
}

/* ------------------------- safe expression evaluation ------------------------- */

// SYNC — every entry runs inside the sandboxed `new Function(...)`.
const AGG_WRAPPED = ["stddev_samp", "stddev_pop", "std", "variance", "var_samp", "var_pop", "median", "product", "prod", "range", "abs_sum", "rms", "count", "sq"];
const MATH_FUNCTIONS = {
  abs: Math.abs, sqrt: Math.sqrt, pow: Math.pow, power: Math.pow, min: Math.min, max: Math.max, greatest: Math.max, least: Math.min,
  floor: Math.floor, ceil: Math.ceil, exp: Math.exp, log: Math.log, log10: Math.log10, log2: Math.log2, sin: Math.sin, cos: Math.cos, tan: Math.tan,
  avg: (...a) => a.reduce((x, y) => x + y, 0) / a.length,
  sum: (...a) => a.reduce((x, y) => x + y, 0),
  round: (v, p) => Number(roundWithDecimal(v, p ?? 0)),
  ...Object.fromEntries(AGG_WRAPPED.map((n) => [n, (...a) => AGGREGATES[n](a)])),
};
const FUNC_NAMES = Object.keys(MATH_FUNCTIONS);
const FUNC_VALUES = FUNC_NAMES.map((k) => MATH_FUNCTIONS[k]);
const FUNC_NAME_RE = new RegExp(`\\b(${FUNC_NAMES.join("|")})\\b`, "gi");
const FUNC_NAME_SET = new Set(FUNC_NAMES.map((f) => f.toLowerCase()));
const FUNC_CALL_CASE_RE = new RegExp(`\\b(${FUNC_NAMES.join("|")})(\\s*\\()`, "gi"); // any-case "SQRT(" -> "sqrt("
const SAFE_ARITHMETIC_RE = /^[\d+\-*/(),.<>=!&|?: \s]+$/;
const isSafeExpr = (expr) => SAFE_ARITHMETIC_RE.test(expr.replace(FUNC_NAME_RE, "")); // SYNC

const CASE_RE = /\bcase\b((?:(?!\bcase\b)[\s\S])*?)\bend\b/i; // innermost CASE ... END
const CASE_KEYWORD_RE = /\b(when|then|else)\b/i;
const CMP_OPERATORS = { "=": "===", "==": "===", "!=": "!==", "<>": "!==", "<=": "<=", ">=": ">=" };

function parseCaseBody(body) { // SYNC — WHEN/THEN branches + ELSE
  const tokens = body.split(CASE_KEYWORD_RE), branches = [];
  let elseVal = null, cond = null;
  for (let i = 1; i < tokens.length; i += 2) {
    const kw = tokens[i].toLowerCase(), text = tokens[i + 1].trim();
    if (kw === "when") cond = text;
    else if (kw === "then" && cond !== null) { branches.push({ cond, val: text }); cond = null; }
    else if (kw === "else") elseVal = text;
  }
  return { branches, elseVal };
}
const sqlConditionToJs = (cond) => cond // SYNC — BETWEEN first so its AND isn't read as logical AND
  .replace(/(\S+)\s+between\s+(\S+)\s+and\s+(\S+)/gi, (_, x, a, b) => `((${x}) >= (${a}) && (${x}) <= (${b}))`)
  .replace(/<=|>=|!=|<>|==|=/g, (op) => CMP_OPERATORS[op])
  .replace(/\band\b/gi, "&&").replace(/\bor\b/gi, "||");
function translateCaseWhen(expr) { // SYNC — CASE...END -> ternary chain; missing ELSE -> 0/0 (NaN = unresolved)
  let out = expr, match;
  while ((match = CASE_RE.exec(out))) {
    const { branches, elseVal } = parseCaseBody(match[1]);
    const ternary = branches.reduceRight((acc, { cond, val }) => `((${sqlConditionToJs(cond)}) ? (${val}) : ${acc})`, elseVal === null ? "(0/0)" : `(${elseVal})`);
    out = out.slice(0, match.index) + ternary + out.slice(match.index + match[0].length);
  }
  return out;
}
function parseWholeCase(expr) { // SYNC — non-null only if the WHOLE expression is one CASE...END
  const t = expr.trim(), m = CASE_RE.exec(t);
  return !m || m.index !== 0 || m[0].length !== t.length ? null : parseCaseBody(m[1]);
}

async function safeEvalArithmetic(rawExpr) {
  const normalized = rawExpr.replace(FUNC_CALL_CASE_RE, (_, name, paren) => name.toLowerCase() + paren);
  const expr = /\bcase\b/i.test(normalized) ? translateCaseWhen(normalized) : normalized;
  if (!isSafeExpr(expr)) { console.error("Unsafe or unrecognized expression:", rawExpr); return null; }
  try {
    const result = Function(...FUNC_NAMES, `"use strict"; return (${expr});`)(...FUNC_VALUES); // eslint-disable-line no-new-func
    return Number.isFinite(result) ? result : null;
  } catch (e) { console.error("Expression evaluation error:", rawExpr, e); return null; }
}

// "AGG(<single-arg expr with colN>)" = evaluate per data row, then reduce (like a SELECT AGG() subquery).
// Multi-arg calls (avg(col2,col3)) stay row-level; GREATEST/LEAST are never aggregates. `dataRows` must be data rows only.
async function resolveColumnAggregateCalls(expression, dataRows) {
  const callRe = new RegExp(`\\b(${Object.keys(AGGREGATES).join("|")})\\s*\\(`, "gi");
  let out = "", cursor = 0, match;
  while ((match = callRe.exec(expression))) {
    const funcName = match[1].toLowerCase(), openIdx = match.index + match[0].length - 1, closeIdx = findMatchingParen(expression, openIdx);
    if (closeIdx === -1) return { expr: null, missing: [`unbalanced parentheses near "${match[0]}"`] };
    const { valueExpr, precisionExpr } = splitTopLevelArgs(expression.slice(openIdx + 1, closeIdx));
    const refs = collectColRefs(valueExpr);
    if (precisionExpr !== null || !refs.length) { out += expression.slice(cursor, closeIdx + 1); cursor = callRe.lastIndex = closeIdx + 1; continue; }
    const bad = unresolvedColumns(dataRows, refs);
    if (bad.length) return { expr: null, missing: [`${funcName}(...): ${bad.join(", ")} not resolved yet`] };
    const values = [];
    for (const row of dataRows) { const v = await safeEvalArithmetic(subCols(valueExpr, row)); if (Number.isFinite(v)) values.push(v); }
    const aggVal = computeAggregate(funcName, values);
    if (!Number.isFinite(aggVal)) return { expr: null, missing: [`${funcName}(...) could not be computed from ${values.length} row value(s)`] };
    out += expression.slice(cursor, match.index) + `(${toPlain(aggVal)})`;
    cursor = callRe.lastIndex = closeIdx + 1;
  }
  return { expr: out + expression.slice(cursor), missing: [] };
}

/* ------------------------------ placeholders ------------------------------ */

// The row's OWN standard-range entries: row.standardRanges = "id:rangeId,..." looked up in the master array.
function getRowStandardRangeEntries(row, standardRanges) {
  if (!row?.standardRanges) return [];
  return String(row.standardRanges).split(",")
    .map((p) => (standardRanges || []).find((r) => r.rangeId === Number(p.trim().split(":")[1])))
    .filter(Boolean);
}

// Order: Math.fn -> fn, $datasheet.x, $standardRanges.x[.all], bare datasheet fields, $stepN.
// `missing` lists unresolved placeholders so callers can skip and retry. With no `row`, every master standardRanges entry is used.
async function resolveStepPlaceholders(expr, datasheet, stepResults, standardRanges, row) {
  const missing = [];
  let out = expr.replace(/\bMath\./g, "");
  out = out.replace(DATASHEET_REF_RE, (_, key) => {
    const num = parseFloat(datasheet?.[key]);
    if (!Number.isFinite(num)) { missing.push(`$datasheet.${key}`); return "0"; }
    return `(${toPlain(num)})`;
  });
  out = out.replace(STANDARD_RANGES_REF_RE, (_, field, scope) => {
    const isAll = (scope ?? "").toLowerCase() === "all";
    const entries = row ? getRowStandardRangeEntries(row, standardRanges) : (standardRanges || []);
    const label = `$standardRanges.${field}${scope ? "." + scope : ""}`;
    if (!entries.length) { missing.push(`${label} (${row ? "no standardRanges on current row" : "no standardRanges available"})`); return "0"; }
    const values = entries.map((r) => parseFloat(String(r?.[field] ?? "").split("#")[0])).filter(Number.isFinite);
    if (!values.length) { missing.push(label); return "0"; }
    // single value is wrapped in parentheses so a negative value never forms "--" or "+-" with the operator before it
    // ".all" stays a bare comma list (used as function arguments), where negatives are already safe
    return isAll ? values.map(toPlain).join(",") : `(${toPlain(values[0])})`;
  });
  out = resolveBareDatasheetFields(out, datasheet, missing);
  out = out.replace(STEP_REF_RE, (_, n) => {
    const val = stepResults[Number(n)];
    if (!Number.isFinite(val)) { missing.push(`$step${n}`); return "0"; }
    return `(${toPlain(val)})`;
  });
  return { expr: out, missing };
}

function resolveBareDatasheetFields(expr, datasheet, missing) { // SYNC — exact datasheet keys that aren't function names
  return expr.replace(BARE_WORD_RE, (word) => {
    if (FUNC_NAME_SET.has(word.toLowerCase()) || !datasheet || !(word in datasheet)) return word;
    const num = parseFloat(datasheet[word]);
    if (!Number.isFinite(num)) { missing.push(`${word} (datasheet field, not numeric yet)`); return word; }
    return `(${toPlain(num)})`;
  });
}

/* ------------------------------- precision ------------------------------- */

// SYNC — stdRangePref: 0 = first standard range, N = Nth, 10 = supportive master.
function getStandardRangeValue(columnKey, standardRanges, standardRangeId, stdRangePref = 0, supportiveRangesId, supportiveStandardMasterArray) {
  const idPart = stdRangePref === 10 ? supportiveRangesId?.split(":")[1] : standardRangeId?.split(",")[stdRangePref > 0 ? stdRangePref - 1 : 0]?.split(":")[1];
  const range = (stdRangePref === 10 ? supportiveStandardMasterArray : standardRanges).find((r) => r.rangeId === Number(idPart));
  return columnKey === "all" ? range : range?.[columnKey]?.split("#")[0] || 0;
}
const pickForTable = (raw, tableIndex) => { const p = String(raw).replace("\n", "").split("||"); return p.length > tableIndex ? p[tableIndex] : p[0]; }; // SYNC
const fractionLength = (s) => (s.split(".")[1] || "").length;

// SYNC — null when the column has no customPrecision rule (see roundToPrecision / the row-formula writer).
function resolveDynamicPrecision({ column, customPrecision, tableIndex, row, datasheet, standardRanges, supportiveStandardMasterArray }) {
  if (!customPrecision?.[column]) return null;
  const parts = String(customPrecision[column]).split(".");
  const [kind, extra] = parts[0].split(EXTRA_PRECISION_SPLITTER);
  const extraPrecision = Number(extra || 0), pref = Number(parts[1] || "0");
  if (Number(kind) === 7) return extraPrecision;
  if (kind.includes("manual")) return parseInt(parts[1], 10) + extraPrecision; // tolerates flags like "manual.2 mround"
  const stdRange = (key) => getStandardRangeValue(key, standardRanges, row.standardRanges, pref, row.supportiveRanges, supportiveStandardMasterArray);
  let rValue;
  if (Number(kind) === 6) {
    const dsLc = pickForTable(datasheet?.lc, tableIndex), rangeLc = String(stdRange("lc") || "0.0");
    rValue = fractionLength(dsLc) > fractionLength(rangeLc) ? dsLc : rangeLc;
  } else {
    if (kind.includes("ds")) rValue = row["c" + kind.split("ds")[1]];
    else if (kind.includes("standardRanges")) rValue = stdRange(kind.split(":")[1]) || 0;
    else switch (Number(kind)) {
      case 1: case 2: rValue = 0; break;
      case 3: rValue = datasheet?.lc; break;
      case 4: rValue = stdRange("lc") || 0; break;
      case 5: rValue = datasheet?.accuracy; break;
      default: rValue = undefined;
    }
    rValue = pickForTable(rValue, tableIndex);
  }
  const s = String(rValue);
  return (s.includes(".") ? s.split(".")[1].split("#")[0].length : 0) + extraPrecision;
}
const precisionOf = (col, cfg, row, ctx) => resolveDynamicPrecision({
  column: col, customPrecision: cfg?.customPrecision, tableIndex: cfg?.index, row,
  datasheet: ctx.datasheet, standardRanges: ctx.standardRanges, supportiveStandardMasterArray: ctx.supportiveStandardMasterArray,
});
// SYNC — always returns a STRING; with no rule, rounds to DEFAULT_PRECISION only if there are more decimals than that.
function roundToPrecision(value, precision) {
  if (precision !== null) return roundWithDecimal(value, precision);
  if (value === null || value === undefined || value === "" || isNaN(value)) return value;
  return (String(value).split(".")[1] || "").length > DEFAULT_PRECISION ? roundWithDecimal(value, DEFAULT_PRECISION) : String(value);
}

/* ------------------------------ public entry ------------------------------ */

export async function processDatasheetByTable(
  staticTables, staticReadingRows, config, setLoader, instrumentId, datasheetId, datasheetDetails,
  standardRanges, supportiveStandardMasterArray, referenceData, selectedStandardMasters // selectedStandardMasters: last, optional
) {
  try {
    // setLoader(true);
    const steps = await fetchSteps(instrumentId);
    return await resolveDatasheet(staticReadingRows, config, steps, datasheetDetails, standardRanges, supportiveStandardMasterArray, referenceData, selectedStandardMasters);
  } catch (error) {
    console.error("Error processing datasheet by table:", error);
    return staticReadingRows;
  }
  //  finally { setLoader(false); }
}

async function fetchSteps(instrumentId) {
  const res = await axiosWithToken.get(`${BASE_URL}datasheetTemplate?_where=(instrumentId,eq,${instrumentId})`);
  return res?.data?.length ? res.data.flatMap((item) => (item.config ? JSON.parse(item.config) : [])) : null;
}

// SYNC — legacy resolveFormulaConditions: a plain string formula passes through; a conditional formula
// ({"lhs == rhs [&& ...]": formula, default}) picks the first fully-matching condition, else "default". Only "==".
// Tokens: standardName | selectedStandardName | <single> | ds.cN | ds.cN.unit | <key>.<field> | <key>.<field>.unit
function resolveFormulaConditions(formulas, referenceData, tableIndex, row, unitRow, standardRanges) {
  const selected = {};
  Object.keys(formulas || {}).forEach((column) => {
    const def = formulas[column];
    if (typeof def === "string") { selected[column] = def; return; }
    Object.keys(def).forEach((condition) => {
      if (condition === "default") return;
      const results = [];
      condition.split(" && ").forEach((sub) => {
        const tokens = sub.split(" == ");
        if (tokens.length !== 2) return;
        const lhs = tokens[0].split(".");
        let lhsVal = null;
        if (lhs.includes("standardName")) lhsVal = (standardRanges || []).map((s) => s.title.trim()).join("||");
        else if (lhs.includes("selectedStandardName")) lhsVal = getRowStandardRangeEntries(row, standardRanges).map((r) => r.title.trim()).join("||");
        else if (lhs.length === 1) lhsVal = lhs; // array; JS "==" coerces it to the token string (legacy behavior)
        else if (lhs.length === 2) lhsVal = lhs[0] === "ds" ? row[`c${lhs[1].split("c")[1]}`] : referenceData?.[lhs[0]]?.[lhs[1]];
        else if (lhs.length === 3) {
          if (lhs[0] === "ds") {
            if (!unitRow) return;
            lhsVal = unitRow[`c${lhs[1].split("c")[1]}`]?.split("_unit_")[1];
          } else {
            lhsVal = referenceData?.[lhs[0]]?.[lhs[1]]?.split("||");
            if (lhs[2] === "unit" && lhsVal) lhsVal = lhsVal.length > 1 ? lhsVal[tableIndex]?.split("#")[1] : lhsVal[0]?.split("#")[1];
          }
        }
        const rhsVal = tokens[1].trim();
        if (Array.isArray(lhsVal) ? lhsVal.join("").includes("||") : String(lhsVal ?? "").includes("||")) {
          if (String(lhsVal).includes(rhsVal)) { selected[column] = def[condition]; results.push(true); } else results.push(false);
        } else results.push(lhsVal == rhsVal);
      });
      if (results.length && !results.includes(false)) selected[column] = def[condition];
    });
    if (!selected[column]) selected[column] = def.default;
  });
  return selected;
}

/* ------------------- row-level formulas + unit conversion ------------------- */

// Replaces each "unitConvertor(cN, type: standard.materialType)" with its converted number. Material type comes
// from the FIRST standard on the row's own standardRanges. Returns null if not ready (retried next pass).
async function resolveMaterialTypeConversions(expr, column, row, unitRow, referenceData, selectedStandardMasters) {
  const matches = [...expr.matchAll(MATERIAL_TYPE_CONVERTOR_RE)];
  if (!matches.length) return expr;
  const standardId = String(row?.standardRanges ?? "").split(",")[0]?.split(":")[0] || null;
  const materialType = (selectedStandardMasters || []).find((s) => s.id == standardId)?.materialType;
  if (!materialType) return null;
  const toUnit = stripUnit(unitRow?.[column]);
  let out = "", cursor = 0;
  for (const m of matches) {
    if (!isResolved(row[m[1]])) return null;
    const converted = Number(await customUnitConversion(numVal(row[m[1]]), stripUnit(unitRow?.[m[1]]), toUnit, row, { ...referenceData, materialType }));
    if (!Number.isFinite(converted)) return null;
    out += expr.slice(cursor, m.index) + `(${toPlain(converted)})`;
    cursor = m.index + m[0].length;
  }
  return out + expr.slice(cursor);
}

// unitCtx (optional) = { unitRow, toUnit, referenceData } enables base-unit conversion: the base unit comes from the first
// source column with a unit; if it is "s" or "°", each source column is converted to it first and the result is converted
// back to the target unit. getBaseUnit/unitConvertor are SYNC, so they are safe inside String.replace.
async function evaluateFormula(expr, row, datasheet, standardRanges, unitCtx = null) {
  const { expr: withPlaceholders, missing } = await resolveStepPlaceholders(expr, datasheet, {}, standardRanges, row);
  if (missing.length) return null;
  let unresolved = false, baseUnit = null;
  const safeExpr = withPlaceholders.replace(/c(\d+)/g, (col) => {
    if (!isResolved(row[col])) { unresolved = true; return "0"; }
    let num = numVal(row[col]);
    const fromUnit = unitCtx && stripUnit(unitCtx.unitRow?.[col]);
    if (fromUnit) {
      baseUnit = baseUnit || getBaseUnit(fromUnit);
      if (BASE_CONVERT_UNITS.includes(baseUnit)) {
        const input = isCustomUnit(fromUnit) ? String(cleanNumeric(row[col])).trim() : num;
        const out = unitConvertor(input, fromUnit, baseUnit);
        // console.log("[unit in]", { input, fromUnit, baseUnit, out }); // remove after debugging
        num = Number(out) || 0;
      }
    }
    return `(${toPlain(num)})`;
  });
  if (unresolved) return null;
  const result = await safeEvalArithmetic(safeExpr);
  if (result === null || !unitCtx) return result;
  const base = baseUnit || unitCtx.toUnit;
  if (!BASE_CONVERT_UNITS.includes(base)) return result;
  const converted = unitConvertor(result, base, unitCtx.toUnit, row, unitCtx.referenceData);
  // console.log("[unit out]", { result, base, toUnit: unitCtx.toUnit, converted }); // remove after debugging
  // custom target units (e.g. "s:ms") come back as a string like "30:157", keep it as-is
  if (isCustomUnit(unitCtx.toUnit)) return converted ? String(converted) : null;
  const n = Number(converted);
  return Number.isFinite(n) ? n : null;
}

// One row-formula cell, old-engine rules:
//  1. unitConvertor(cN, type: standard.materialType) is resolved first.
//  2. A direct "cN" mapping between a custom unit pair (e.g. °C <-> Ω) goes through customUnitConversion (wins over 3).
//  3. Default (fallback) formulas whose base unit is "s"/"°" convert via the base unit.
// Returns a number, or null when not ready / not convertible (cell skipped, retried next pass).
async function computeRowFormula(expr, col, row, unitRow, isDefault, ctx) {
  const { datasheet, standardRanges, referenceData, selectedStandardMasters } = ctx;
  const toUnit = stripUnit(unitRow?.[col]);
  const prepared = await resolveMaterialTypeConversions(expr, col, row, unitRow, referenceData, selectedStandardMasters);
  if (prepared === null) return null;
  const source = prepared.trim(), fromUnit = /^c\d+$/.test(source) ? stripUnit(unitRow?.[source]) : null;
  if (fromUnit && toUnit && containsCustomUnitPair(fromUnit, toUnit)) {
    const raw = await evaluateFormula(prepared, row, datasheet, standardRanges);
    if (raw === null) return null;
    const converted = Number(await customUnitConversion(raw, fromUnit, toUnit, row, referenceData));
    return Number.isFinite(converted) ? converted : null;
  }
  return evaluateFormula(prepared, row, datasheet, standardRanges, isDefault && toUnit ? { unitRow, toUnit, referenceData } : null);
}

// SYNC — maps each data row to the nearest unit row ABOVE it (falls back to the first unit row if none precedes it)
function buildUnitRowMap(tableRows) {
  const map = new Map(), first = findUnitRow(tableRows);
  let current = null;
  for (const r of tableRows) {
    if (typeof r.c1 === "string" && r.c1.includes("_unit_")) current = r;
    else if (isDataRow(r)) map.set(r, current || first);
  }
  return map;
}

/* -------------------- core resolver: fixed-point loop -------------------- */

async function resolveDatasheet(staticReadingRows, config, steps, datasheet, standardRanges, supportiveStandardMasterArray, referenceData, selectedStandardMasters) {
  const rows = (staticReadingRows || []).map((r) => ({ ...r })); // clone; caller's rows never mutated
  const rowsByTable = {}, dataByTable = {}, configByTable = {}, tableIdByIndex = {}; // "FROM t1" is positional: position N -> config index N-1
  rows.forEach((r) => (rowsByTable[r.tableId] ??= []).push(r));
  (config || []).forEach((cfg) => (configByTable[cfg.tableId] = cfg));
  Object.values(configByTable).forEach((cfg) => { if (typeof cfg.index === "number" && !(cfg.index in tableIdByIndex)) tableIdByIndex[cfg.index] = cfg.tableId; });

  const formulaTables = [], precisionOnlyTables = []; // precisionOnly: manual cols with a customPrecision rule but no formula
  Object.entries(rowsByTable).forEach(([tableId, tableRows]) => {
    const cfg = configByTable[tableId], formulaCols = new Set(Object.keys(cfg?.formulas || {})), dataRows = (dataByTable[tableId] = tableRows.filter(isDataRow));
   if (formulaCols.size) formulaTables.push({ tableRows: dataRows, cfg, unitRowOf: buildUnitRowMap(tableRows) });
    const precisionCols = Object.keys(cfg?.customPrecision || {}).filter((c) => !formulaCols.has(c));
    if (precisionCols.length) precisionOnlyTables.push({ tableRows: dataRows, cfg, precisionCols });
  });

  const parsedSteps = (steps || []).map((s, i) => parseStep(s, i + 1)).filter(Boolean); // position taken before filtering
  const ctx = {
    rowsByTable, dataByTable, configByTable, tableIdByIndex, datasheet, standardRanges, supportiveStandardMasterArray, referenceData, selectedStandardMasters,
    stepResults: {}, finalValues: {}, skipReasons: {}, logged: new Set(),
  };

  let changed = true, iter = 0;
  while (changed && iter++ < MAX_ITER) {
    changed = false;

    for (const { tableRows, cfg, unitRowOf } of formulaTables)
      for (const row of tableRows) {
        const unitRow = unitRowOf.get(row); // nearest previous unit row for THIS data row
        for (const [col, expr] of Object.entries(resolveFormulaConditions(cfg.formulas, referenceData, cfg.index, row, unitRow, standardRanges))) {
          if (!expr) continue;
          // like the old engine: base-unit conversion only for conditional formulas that fell through to "default"
          const value = await computeRowFormula(expr, col, row, unitRow, expr === cfg.formulas[col]?.default, ctx);
          if (value === null) continue;
          const precision = precisionOf(col, cfg, row, ctx);
          const newVal = typeof value === "string"
            ? value // custom unit results (e.g. "30:157") are kept as-is
            : precision === null ? toPlain(cleanFloat(value)) : roundWithDecimal(value, precision);
          if (row[col] !== newVal) { row[col] = newVal; changed = true; }
        }
      }

    for (const { tableRows, cfg, precisionCols } of precisionOnlyTables)
      for (const row of tableRows)
        for (const col of precisionCols) {
          const newVal = roundStoredValue(row[col], cfg.customPrecision[col], precisionOf(col, cfg, row, ctx));
          if (newVal !== null) { row[col] = newVal; changed = true; }
        }

    for (const step of parsedSteps) if (await applyStep(step, ctx)) changed = true;
  }
  if (changed) console.warn("resolveDatasheet: hit MAX_ITER, some values may be unresolved (check for cycles / missing data)");
  return rows; // ctx.skipReasons / ctx.stepResults / ctx.finalValues are available here for debug logging
}

/* ------------------------------ step helpers ------------------------------ */

const skip = (ctx, step, reason) => ((ctx.skipReasons[step.stepNo] = reason), false);
const sourceId = (step, ctx) => step.sourceTableId ?? ctx.tableIdByIndex[step.sourceTableIndex - 1];
const noSource = (step, ctx) => `no table config with index ${step.sourceTableIndex - 1} for "t${step.sourceTableIndex}" (config indexes found: ${Object.keys(ctx.tableIdByIndex).join(",") || "none"})`;
const record = (ctx, step, raw, fin, changed) => { ctx.stepResults[step.stepNo] = raw; ctx.finalValues[step.stepNo] = fin; return changed; };

// writer(row, val) -> true if the cell changed; always records raw/final values (null when val isn't a finite number or text)
const rowWriter = (col, cfg, ctx, raw, fin) => (row, val) => {
  const text = typeof val === "string";
  if (val === null || val === undefined || (!text && !Number.isFinite(val))) { raw.push(null); fin.push(null); return false; }
  raw.push(val);
  const nv = text ? val : roundToPrecision(val, precisionOf(col, cfg, row, ctx));
  fin.push(nv);
  if (row[col] === nv) return false;
  row[col] = nv;
  return true;
};

// SYNC — e.g. ["col3 (reads c2; row ids: 4,5)"] for columns unresolved on some rows.
function unresolvedColumns(rows, colNums) {
  return colNums.flatMap((n) => {
    const ids = rows.filter((r) => !isResolved(readCol(r, n))).map((r) => r.id);
    return ids.length ? [`col${n} (reads ${colSource(rows[0], n)}; row ids: ${ids.slice(0, 5).join(",")}${ids.length > 5 ? ",..." : ""})`] : [];
  });
}
function cellPreview(row) { // SYNC — e.g. 'c0="" c1="$" c2="4.5"'
  const cells = Object.keys(row).filter((k) => /^c\d+$/.test(k)).sort((a, b) => a.slice(1) - b.slice(1)).slice(0, 12).map((k) => `${k}=${JSON.stringify(row[k])}`);
  if (row.uncertainty !== undefined) cells.push(`uncertainty=${JSON.stringify(row.uncertainty)}`);
  return cells.join(" ");
}

// aggregate: "(SELECT AGG(colN) FROM tidM)" | rowFilter: "SELECT <expr> AS colM FROM t1 [WHERE colX=val] [LIMIT n]"
// groupedProjection: "... GROUP BY ..." (or self-join) | windowedModuloProjection: "Nth row per group" | sqlQuery: any other SELECT
const applyStep = (step, ctx) => ({ rowFilter: applyRowFilterStep, groupedProjection: applyGroupedProjection, windowedModuloProjection: applyWindowedModuloProjection, sqlQuery: applySqlQueryStep }[step.mode] || applyAggregateStep)(step, ctx);

// "Nth row per group": partition by groupByColNum (ordered by id), keep rows where (rn-1) % modulus === remainder,
// emit group by group (groups ordered by min id) and write positionally into the target table's data rows.
async function applyWindowedModuloProjection(step, ctx) {
  const { rowsByTable, dataByTable, configByTable } = ctx;
  const { groupByColNum, aggColNum: selCol, modulus, remainder, targetTableId, targetCol, stepNo, scope } = step;
  const srcId = sourceId(step, ctx);
  if (srcId === undefined) return skip(ctx, step, noSource(step, ctx));
  const sourceRows = dataByTable[srcId], dataRows = dataByTable[targetTableId];
  if (!sourceRows?.length) return skip(ctx, step, `source table ${srcId} has no rows`);
  if (!rowsByTable[targetTableId]) return skip(ctx, step, `target table ${targetTableId} has no rows`);
  const bad = unresolvedColumns(sourceRows, [groupByColNum, selCol]);
  if (bad.length) return skip(ctx, step, `source table ${srcId}: ${bad.join(", ")} not resolved yet`);

  const selected = [...groupBy(sourceRows, (r) => cleanNumeric(String(readCol(r, groupByColNum)))).values()]
    .map((g) => [...g].sort((a, b) => a.id - b.id)).sort((a, b) => a[0].id - b[0].id)
    .flatMap((g) => g.filter((_, i) => i % modulus === remainder).map((r) => stepNumVal(readCol(r, selCol))));

  const raw = [], fin = [], write = rowWriter(targetCol, configByTable[targetTableId], ctx, raw, fin);
  let changed = false;
  if (scope === "all") {
    dataRows.forEach((row, i) => { changed = write(row, selected[i]) || changed; });
    if (selected.length !== dataRows.length) ctx.skipReasons[stepNo] = `windowed selection produced ${selected.length} value(s) but target table ${targetTableId} has ${dataRows.length} data row(s) — ${selected.length > dataRows.length ? "extra value(s) dropped" : "trailing row(s) left unset"}`;
  } else if (dataRows[scope - 1]) changed = write(dataRows[scope - 1], selected[scope - 1]);
  else ctx.skipReasons[stepNo] = `scope "row${scope}" is out of range — only ${dataRows.length} data row(s) found`;
  return record(ctx, step, raw, fin, changed);
}

// SYNC — one composite GROUP BY key part: "colN" -> that column; "unit" -> this row's own unit
// (from the nearest unit row above it); else a row property.
function readGroupKeyPart(row, part, unitOf) {
  const m = /^col(\d+)$/i.exec(part);
  if (m) return readCol(row, Number(m[1]));
  if (part.toLowerCase() === "unit") return unitOf(row);
  return row[part];
}

async function applyGroupedProjection(step, ctx) {
  const { rowsByTable, dataByTable, configByTable } = ctx;
  const { aggFunc, aggColNum, groupByParts, targetTableId, targetCol, stepNo, scope } = step;
  if (step.isPassthrough) return skip(ctx, step, "GROUP BY steps must write into a table (outputStream needs a target column), not passthrough");
  const srcId = sourceId(step, ctx);
  if (srcId === undefined) return skip(ctx, step, noSource(step, ctx));
  const sourceRows = dataByTable[srcId], dataRows = dataByTable[targetTableId];
  if (!sourceRows?.length) return skip(ctx, step, `source table ${srcId} has no rows`);
  if (!rowsByTable[targetTableId]) return skip(ctx, step, `target table ${targetTableId} has no rows`);

  // "unit" = unit of the GROUP-KEY column (first colN in GROUP BY, e.g. col2 in "GROUP BY col2, unit"),
  // read from the NEAREST unit row ABOVE each data row ("_unit_mg" -> "mg"). Falls back to the aggregated
  // column when GROUP BY has no colN. If the unit row changes (and its unit differs), the key changes -> new group.
  const keyColPart = groupByParts.map((p) => /^col(\d+)$/i.exec(p)).find(Boolean);
  const unitColNum = keyColPart ? Number(keyColPart[1]) : aggColNum;
  const unitRowOf = buildUnitRowMap(rowsByTable[srcId]);
  const unitOf = (row) => {
    const unitRow = unitRowOf.get(row);
    return unitRow ? (String(readCol(unitRow, unitColNum) ?? "").split("_unit_")[1] || "").trim() || null : null;
  };

  const keyBad = groupByParts.filter((p) => sourceRows.some((r) => !hasValue(readGroupKeyPart(r, p, unitOf))));
  const aggBad = unresolvedColumns(sourceRows, [aggColNum]);
  if (keyBad.length || aggBad.length) {
    const parts = [...keyBad.map((p) => `group key "${p}" not resolved${/^col(\d+)$/i.test(p) ? " on every row" : ` (no "_unit_" marker found for col${unitColNum} on table ${srcId})`}`), ...aggBad];
    return skip(ctx, step, `source table ${srcId}: ${parts.join(", ")} not resolved yet`);
  }

  const groupKey = (row) => groupByParts.map((p) => cleanNumeric(String(readGroupKeyPart(row, p, unitOf)))).join("\u241F");
  const resultByGroup = new Map([...groupBy(sourceRows, groupKey, (r) => stepNumVal(readCol(r, aggColNum)))].map(([k, v]) => [k, computeAggregate(aggFunc, v)]));

  const raw = [], fin = [], write = rowWriter(targetCol, configByTable[targetTableId], ctx, raw, fin);
  let changed = false;
  if (scope === "all") {
    if (srcId === targetTableId) {
      dataRows.forEach((row, i) => {
        const s = sourceRows[i];
        if (!s) { raw.push(null); fin.push(null); return; }
        changed = write(row, resultByGroup.get(groupKey(s))) || changed;
      });
    } else {
      const groupValues = [...resultByGroup.values()];
      dataRows.forEach((row, i) => { changed = write(row, groupValues[i]) || changed; });
      if (groupValues.length !== dataRows.length) ctx.skipReasons[stepNo] = `source table ${srcId} produced ${groupValues.length} distinct group(s) but target table ${targetTableId} has ${dataRows.length} data row(s) — ${groupValues.length > dataRows.length ? "extra group(s) dropped" : "trailing row(s) left unset"}`;
    }
  } else if (dataRows[scope - 1] && sourceRows[scope - 1]) changed = write(dataRows[scope - 1], resultByGroup.get(groupKey(sourceRows[scope - 1])));
  else ctx.skipReasons[stepNo] = `scope "row${scope}" is out of range — only ${dataRows.length} data row(s) found (ids: ${dataRows.map((r) => r.id).join(",") || "none"})`;
  return record(ctx, step, raw, fin, changed);
}

// Stores the step's raw/rounded result and writes it into target rows.
async function commitStepResult(step, rawAns, targetRows, ctx) {
  const { stepNo, targetTableId, targetCol, scope } = step, cfg = ctx.configByTable[targetTableId];
  let changed = ctx.stepResults[stepNo] !== rawAns, finalVal = null;
  ctx.stepResults[stepNo] = rawAns;
  const writeToRow = (row) => {
    const nv = roundToPrecision(rawAns, precisionOf(targetCol, cfg, row, ctx));
    finalVal ??= nv;
    if (row[targetCol] !== nv) { row[targetCol] = nv; changed = true; }
  };
  if (scope === "all") {
    const dataRows = ctx.dataByTable[targetTableId];
    dataRows.forEach(writeToRow);
    if (!dataRows.length) ctx.skipReasons[stepNo] = `computed ${rawAns}, but target table ${targetTableId} has no data rows to write it into`;
  } else { // "rowN" = Nth row as stored (unit/header rows included), NOT the Nth data row
    const row = targetRows[scope - 1];
    if (row && isDataRow(row)) writeToRow(row);
    else ctx.skipReasons[stepNo] = row
      ? `computed ${rawAns}, but row ${scope} of table ${targetTableId} is a unit/header row, not a data row`
      : `computed ${rawAns}, but scope "row${scope}" is out of range for table ${targetTableId} — only ${targetRows.length} row(s) found (ids: ${targetRows.map((r) => r.id).join(",") || "none"})`;
  }
  ctx.finalValues[stepNo] = finalVal;
  return changed;
}

// Passthrough (no "tid" in outputStream): writes nowhere; the value only feeds later "$stepN".
async function commitPassthroughResult(step, rawAns, ctx) {
  const changed = ctx.stepResults[step.stepNo] !== rawAns;
  ctx.stepResults[step.stepNo] = ctx.finalValues[step.stepNo] = rawAns;
  return changed;
}

async function applyAggregateStep(step, ctx) {
  const { rowsByTable, dataByTable, datasheet, stepResults, standardRanges } = ctx;
  let targetRows = null;
  if (!step.isPassthrough && !(targetRows = rowsByTable[step.targetTableId])) return skip(ctx, step, `target table ${step.targetTableId} has no rows`);
  for (const { colNum, tableId } of step.deps) {
    const rows = dataByTable[tableId];
    if (!rows?.length) return skip(ctx, step, `source table ${tableId} has no rows`);
    const bad = unresolvedColumns(rows, [colNum]);
    if (bad.length) return skip(ctx, step, `table ${tableId}: ${bad.join(", ")} not resolved yet`);
  }
  const { expr: expression, missing } = await resolveStepPlaceholders(step.expression, datasheet, stepResults, standardRanges);
  if (missing.length) return skip(ctx, step, `waiting for ${missing.join(", ")}`);
  const rawAns = await resolveExpression(expression, ctx);
  if (!Number.isFinite(rawAns)) return skip(ctx, step, `expression could not be evaluated: ${expression}${ctx.lastExpressionReason ? ` — ${ctx.lastExpressionReason}` : ""}`);
  return step.isPassthrough ? commitPassthroughResult(step, rawAns, ctx) : commitStepResult(step, rawAns, targetRows, ctx);
}

// "Find the row where the condition holds": first row matching a WHEN branch wins; else ELSE.
async function evaluateCaseAcrossRows(spec, rows) {
  const notReady = (row, expr) => {
    const bad = collectColRefs(expr).filter((n) => !isResolved(readCol(row, n)));
    return bad.length ? `row ${row.id}: ${bad.map((n) => `col${n} (reads ${colSource(row, n)} = ${JSON.stringify(readCol(row, n))})`).join(", ")} not resolved yet` : null;
  };
  for (const row of rows) for (const { cond, val } of spec.branches) {
    const waiting = notReady(row, cond);
    if (waiting) return { reason: waiting };
    const hit = await safeEvalArithmetic(`((${sqlConditionToJs(subCols(cond, row))}) ? 1 : 0)`);
    if (hit === null) return { reason: `row ${row.id}: condition could not be evaluated: ${cond}` };
    if (hit !== 1) continue;
    const waitingVal = notReady(row, val);
    if (waitingVal) return { reason: waitingVal };
    const value = await safeEvalArithmetic(subCols(val, row));
    return value === null ? { reason: `row ${row.id}: THEN value could not be evaluated: ${val}` } : { value };
  }
  if (spec.elseVal === null) return { reason: "no row matched a WHEN branch and there is no ELSE" };
  const waiting = notReady(rows[0], spec.elseVal);
  if (waiting) return { reason: waiting };
  const value = await safeEvalArithmetic(subCols(spec.elseVal, rows[0]));
  return value === null ? { reason: `ELSE value could not be evaluated: ${spec.elseVal}` } : { value };
}

async function applyRowFilterStep(step, ctx) {
  const { rowsByTable, dataByTable, datasheet, stepResults, standardRanges } = ctx;
  const { whereCol, whereVal, limit, depColNums, targetTableId, scope } = step;
  const ph = await resolveStepPlaceholders(step.expression, datasheet, stepResults, standardRanges);
  if (ph.missing.length) return skip(ctx, step, `waiting for ${ph.missing.join(", ")}`);
  let expression = ph.expr;
  const srcId = sourceId(step, ctx);
  if (srcId === undefined) return skip(ctx, step, noSource(step, ctx));
  if (!rowsByTable[srcId]?.length) return skip(ctx, step, `source table ${srcId} has no rows`);
  let candidates = dataByTable[srcId];
  const failEval = () => skip(ctx, step, `expression could not be evaluated: ${expression}`);

  if (step.isPassthrough) { // old engine only ever used the FIRST data row of the FROM table
    if (!candidates.length) return skip(ctx, step, `source table ${srcId} has only unit/header rows`);
    const bad = depColNums.length ? unresolvedColumns([candidates[0]], depColNums) : [];
    if (bad.length) return skip(ctx, step, `table ${srcId}: ${bad.join(", ")} not resolved yet`);
    const rawAns = await safeEvalArithmetic(subCols(expression, candidates[0]));
    return Number.isFinite(rawAns) ? commitPassthroughResult(step, rawAns, ctx) : failEval();
  }

  const targetRows = rowsByTable[targetTableId];
  if (!targetRows) return skip(ctx, step, `target table ${targetTableId} has no rows`);
  if (!candidates.length) return skip(ctx, step, `table ${srcId} has only unit/header rows`);
  if (whereCol !== null) {
    const bad = unresolvedColumns(candidates, [whereCol]);
    if (bad.length) return skip(ctx, step, `table ${srcId}: WHERE ${bad.join(", ")} not resolved yet`);
    candidates = candidates.filter((r) => stepNumVal(readCol(r, whereCol)) === whereVal);
    if (!candidates.length) return skip(ctx, step, `no rows matched WHERE col${whereCol} = ${whereVal}`);
  }

  const agg = await resolveColumnAggregateCalls(expression, candidates); // stddev_samp(col3), max(abs(col7)), ...
  if (agg.missing.length) return skip(ctx, step, `table ${srcId}: ${agg.missing.join(", ")}`);
  expression = agg.expr;

  const remainingCols = collectColRefs(expression);
  if (!remainingCols.length) { // every colN absorbed -> one constant (no-GROUP-BY aggregate)
    const rawAns = await safeEvalArithmetic(expression);
    return Number.isFinite(rawAns) ? commitStepResult(step, rawAns, targetRows, ctx) : failEval();
  }
  const caseSpec = parseWholeCase(expression); // whole-expression CASE: first table match wins
  if (caseSpec) {
    const outcome = await evaluateCaseAcrossRows(caseSpec, candidates);
    return "reason" in outcome ? skip(ctx, step, `table ${srcId}: ${outcome.reason}`) : commitStepResult(step, outcome.value, targetRows, ctx);
  }
  if (!limit && scope === "all") return applyProjection(step, candidates, targetRows, srcId, expression, ctx); // per-row projection
  if (limit) candidates = candidates.slice(0, limit);
  const bad = unresolvedColumns(candidates, remainingCols);
  if (bad.length) return skip(ctx, step, `table ${srcId}: ${bad.join(", ")} not resolved yet | row ${candidates[0].id} cells: ${cellPreview(candidates[0])}`);

  let rawAns = null;
  for (const r of candidates) { const v = await safeEvalArithmetic(subCols(expression, r)); if (Number.isFinite(v)) { rawAns = v; break; } }
  return rawAns === null ? skip(ctx, step, `expression evaluation failed for every candidate row: ${expression}`) : commitStepResult(step, rawAns, targetRows, ctx);
}

// Per-row projection: result[i] (from source row i's own columns) -> i-th target data row. Not-ready rows stay blank and retry later.
async function applyProjection(step, sourceRows, targetRows, srcId, expression, ctx) {
  const { targetTableId, targetCol } = step;
  const dataRows = ctx.dataByTable[targetTableId], needed = collectColRefs(expression), raw = [], fin = [];
  const write = rowWriter(targetCol, ctx.configByTable[targetTableId], ctx, raw, fin);
  let changed = false, anyResolved = false;
  for (let i = 0; i < dataRows.length; i++) {
    const src = sourceRows[i];
    const val = src && needed.every((n) => isResolved(readCol(src, n))) ? await safeEvalArithmetic(subCols(expression, src)) : null;
    if (Number.isFinite(val)) anyResolved = true;
    changed = write(dataRows[i], val) || changed;
  }
  if (!anyResolved) {
    const wanted = needed.map((n) => `col${n} (reads ${colSource(sourceRows[0], n)})`).join(", ");
    const dump = sourceRows.slice(0, 5).map((r) => `row ${r.id}: ${cellPreview(r)}`).join(" | ");
    skip(ctx, step, `source table ${srcId}: found ${sourceRows.length} data row(s), target table ${targetTableId} has ${dataRows.length} data row(s); none of the source rows has ${wanted} resolved yet — ${dump}${sourceRows.length > 5 ? " | ..." : ""}`);
  }
  return record(ctx, step, raw, fin, changed);
}

/* ------------------------- general SQL query engine ------------------------- */
// Fallback for any SELECT the fixed shapes can't parse: derived tables (FROM (SELECT ...)), SELECT *, CASE, CAST,
// BETWEEN/IN/IS NULL, AND/OR/NOT, window aggregates (AGG(x) OVER ([PARTITION BY ..] [ORDER BY ..])), ROW_NUMBER(),
// GROUP BY, WHERE, ORDER BY, LIMIT, scalar subqueries and $datasheet/$stepN/$standardRanges placeholders.

class NotReady extends Error {}
const SQL_KEYWORDS = new Set(["select", "from", "where", "group", "by", "order", "limit", "offset", "as", "and", "or", "not", "case", "when", "then", "else", "end", "between", "in", "is", "null", "cast", "over", "partition", "asc", "desc", "true", "false"]);
const NON_ALIAS_RE = /^(join|inner|left|right|cross|full|union|having|offset)$/i;
const CMP_FN = { "=": (c) => c === 0, "==": (c) => c === 0, "!=": (c) => c !== 0, "<>": (c) => c !== 0, "<": (c) => c < 0, "<=": (c) => c <= 0, ">": (c) => c > 0, ">=": (c) => c >= 0 };
const ARITH = { "+": (x, y) => x + y, "-": (x, y) => x - y, "*": (x, y) => x * y, "/": (x, y) => (y === 0 ? NaN : x / y), "%": (x, y) => (y === 0 ? NaN : x % y) }; // /0 -> NULL
// NULL in -> NULL out (MySQL-like string functions)
const strFn = (fn) => (s, ...rest) => (s === null || s === undefined ? null : fn(String(s), ...rest));

const SCALAR_EXTRA = {
  coalesce: (...a) => a.find((v) => v !== null && v !== undefined) ?? null,
  ifnull: (a, b) => a ?? b, nullif: (a, b) => (sqlCompare(a, b) === 0 ? null : a),
  if: (c, a, b) => (truthy(c) ? a : b), mod: (a, b) => (Number(b) === 0 ? null : a % b),
  // REPLACE(str, from, to) — NULL in, NULL out (like MySQL)
  replace: (s, from, to) => (s === null || s === undefined || from === null || from === undefined || to === null || to === undefined ? null : String(s).split(String(from)).join(String(to))),

  // --- string functions (NEW) ---
  trim: strFn((s) => s.trim()),
  ltrim: strFn((s) => s.trimStart()),
  rtrim: strFn((s) => s.trimEnd()),
  upper: strFn((s) => s.toUpperCase()),
  ucase: strFn((s) => s.toUpperCase()),
  lower: strFn((s) => s.toLowerCase()),
  lcase: strFn((s) => s.toLowerCase()),
  length: strFn((s) => s.length),
  char_length: strFn((s) => s.length),
  left: strFn((s, n) => s.slice(0, Math.max(0, Number(n)))),
  right: strFn((s, n) => (Number(n) <= 0 ? "" : s.slice(-Number(n)))),
  // SUBSTRING(str, pos[, len]) — 1-based like MySQL
  substring: strFn((s, pos, len) => {
    const start = Number(pos) > 0 ? Number(pos) - 1 : Number(pos) < 0 ? Math.max(0, s.length + Number(pos)) : s.length;
    return len === undefined ? s.slice(start) : s.slice(start, start + Math.max(0, Number(len)));
  }),
  substr: (...a) => SCALAR_EXTRA.substring(...a),
  concat: (...a) => (a.some((v) => v === null || v === undefined) ? null : a.join("")),
};
const isAggName = (name) => Object.prototype.hasOwnProperty.call(AGGREGATES, name);
const truthy = (v) => v !== null && v !== undefined && v !== 0 && v !== "" && v !== false;
const cellValue = (v) => { // number | text | null (NULL while the cell is empty or "$")
  if (!hasValue(v)) return null;
  const c = cleanNumeric(v);
  return String(c).trim() !== "" && Number.isFinite(Number(c)) ? stepNumVal(c) : String(v);
};
const walk = (n, fn) => { if (!n || typeof n !== "object") return; if (Array.isArray(n)) { n.forEach((x) => walk(x, fn)); return; } fn(n); Object.values(n).forEach((v) => walk(v, fn)); };

function tokenizeSql(sql) {
  const re = /\s*(?:(\d+(?:\.\d+)?(?:e[+-]?\d+)?)|'((?:[^']|'')*)'|"((?:[^"]|"")*)"|(\$?[A-Za-z_]\w*(?:\.\w+)*)|(<=|>=|<>|!=|==|[-+*\/%(),.=<>]))/gy;
  const toks = []; let m, end = 0;
  while ((m = re.exec(sql))) {
    end = re.lastIndex;
    if (m[1] !== undefined) toks.push({ t: "num", v: m[1] });
    else if (m[2] !== undefined) toks.push({ t: "str", v: m[2].replace(/''/g, "'") });
    else if (m[3] !== undefined) toks.push({ t: "str", v: m[3].replace(/""/g, '"') });
    else if (m[4] !== undefined) toks.push(SQL_KEYWORDS.has(m[4].toLowerCase()) ? { t: "kw", v: m[4].toLowerCase() } : { t: "id", v: m[4] });
    else toks.push({ t: "op", v: m[5] });
  }
  if (sql.slice(end).trim()) throw new Error(`unexpected character near "${sql.slice(end).trim().slice(0, 20)}"`);
  return toks;
}

function parseSql(sql) {
  const toks = tokenizeSql(sql); let p = 0;
  const peek = (o = 0) => toks[p + o];
  const isKw = (k, o = 0) => peek(o)?.t === "kw" && peek(o).v === k;
  const isOp = (v, o = 0) => peek(o)?.t === "op" && peek(o).v === v;
  const eatKw = (k) => isKw(k) && (p++, true);
  const eatOp = (v) => isOp(v) && (p++, true);
  const need = (c, msg) => { if (!c) throw new Error(`${msg} near "${peek()?.v ?? "end of query"}"`); };
  const expectKw = (k) => need(eatKw(k), `expected ${k.toUpperCase()}`);
  const expectOp = (v) => need(eatOp(v), `expected "${v}"`);
  const list = (parseItem) => { const out = []; do out.push(parseItem()); while (eatOp(",")); return out; };

  const parseOr = () => { let l = parseAnd(); while (eatKw("or")) l = { k: "bin", op: "||", l, r: parseAnd() }; return l; };
  const parseAnd = () => { let l = parseNot(); while (eatKw("and")) l = { k: "bin", op: "&&", l, r: parseNot() }; return l; };
  const parseNot = () => (eatKw("not") ? { k: "not", e: parseNot() } : parseCmp());
  function parseCmp() {
    const l = parseAdd();
    const neg = isKw("not") && (isKw("between", 1) || isKw("in", 1)) && (p++, true);
    if (eatKw("between")) { const a = parseAdd(); expectKw("and"); return { k: "between", e: l, a, b: parseAdd(), neg }; }
    if (eatKw("in")) { expectOp("("); const items = list(parseOr); expectOp(")"); return { k: "in", e: l, list: items, neg }; }
    if (eatKw("is")) { const n = eatKw("not"); expectKw("null"); return { k: "isnull", e: l, neg: n }; }
    const t = peek();
    if (t?.t === "op" && CMP_FN[t.v]) { p++; return { k: "cmp", op: t.v, l, r: parseAdd() }; }
    return l;
  }
  const parseAdd = () => { let l = parseMul(); while (isOp("+") || isOp("-")) { const op = toks[p++].v; l = { k: "bin", op, l, r: parseMul() }; } return l; };
  const parseMul = () => { let l = parseUnary(); while (isOp("*") || isOp("/") || isOp("%")) { const op = toks[p++].v; l = { k: "bin", op, l, r: parseUnary() }; } return l; };
  const parseUnary = () => (eatOp("-") ? { k: "neg", e: parseUnary() } : eatOp("+") ? parseUnary() : parsePrimary());
  function parsePrimary() {
    const t = toks[p++];
    need(t, "unexpected end of query");
    if (t.t === "num") return { k: "lit", v: Number(t.v) };
    if (t.t === "str") return { k: "lit", v: t.v };
    if (t.t === "kw") {
      if (t.v === "null") return { k: "lit", v: null };
      if (t.v === "true" || t.v === "false") return { k: "lit", v: t.v === "true" ? 1 : 0 };
      if (t.v === "case") return parseCase();
      if (t.v === "cast") return parseCast();
    }
    if (t.t === "op" && t.v === "(") {
      if (isKw("select")) { const q = parseQuery(); expectOp(")"); return { k: "subq", q }; }
      const e = parseOr(); expectOp(")"); return e;
    }
    if (t.t === "id") return isOp("(") ? parseCall(t.v) : { k: "id", name: t.v };
    throw new Error(`unexpected "${t.v}"`);
  }
  function parseCase() {
    const subject = isKw("when") ? null : parseOr(), branches = [];
    while (eatKw("when")) { const c = parseOr(); expectKw("then"); branches.push({ c, v: parseOr() }); }
    const els = eatKw("else") ? parseOr() : null;
    expectKw("end");
    return { k: "case", subject, branches, els };
  }
  function parseCast() {
    expectOp("("); const e = parseOr(); expectKw("as");
    const t = toks[p++]; need(t, "expected a type");
    const args = eatOp("(") ? (() => { const a = list(() => Number(toks[p++].v)); expectOp(")"); return a; })() : [];
    if (peek()?.t === "id") p++; // "SIGNED INTEGER"
    expectOp(")");
    return { k: "cast", e, type: t.v.toLowerCase(), scale: args[1] ?? 0 };
  }
  const parseOrderItem = () => ({ e: parseOr(), desc: eatKw("desc") || (eatKw("asc"), false) });
  function parseCall(name) {
    expectOp("(");
    const args = isOp("*") ? (p++, [{ k: "star" }]) : isOp(")") ? [] : list(parseOr);
    expectOp(")");
    const fn = { k: "call", name: name.toLowerCase(), args };
    if (!eatKw("over")) return fn;
    expectOp("(");
    const part = eatKw("partition") ? (expectKw("by"), list(parseOr)) : [];
    const order = eatKw("order") ? (expectKw("by"), list(parseOrderItem)) : [];
    expectOp(")");
    return { k: "over", fn, part, order };
  }
  function parseQuery() {
    expectKw("select");
    const items = list(() => {
      if (isOp("*")) { p++; return { star: true }; }
      if (peek()?.t === "id" && isOp(".", 1) && isOp("*", 2)) { p += 3; return { star: true }; }
      const e = parseOr();
      const alias = eatKw("as") ? toks[p++]?.v : peek()?.t === "id" ? toks[p++].v : null;
      return { e, alias: alias ?? (e.k === "id" ? e.name.split(".").pop() : `_c${p}`) };
    });
    expectKw("from");
    const skipAlias = () => { eatKw("as"); if (peek()?.t === "id" && !NON_ALIAS_RE.test(peek().v)) p++; };
    let from;
    if (eatOp("(")) { const sub = parseQuery(); expectOp(")"); skipAlias(); from = { sub }; }
    else {
      const t = toks[p++], m = /^t(id)?(\d+)$/i.exec(t?.v ?? "");
      need(m, `unsupported FROM source "${t?.v}"`);
      skipAlias();
      from = m[1] ? { id: +m[2] } : { index: +m[2] };
    }
    const q = { items, from, where: eatKw("where") ? parseOr() : null, groupBy: [], order: [], limit: null, offset: 0 };
    if (eatKw("group")) { expectKw("by"); q.groupBy = list(parseOr); }
    if (eatKw("order")) { expectKw("by"); q.order = list(parseOrderItem); }
    if (eatKw("limit")) {
      q.limit = Number(toks[p++]?.v);
      if (eatOp(",")) { q.offset = q.limit; q.limit = Number(toks[p++]?.v); } // MySQL "LIMIT offset, count"
    }
    if (eatKw("offset")) q.offset = Number(toks[p++]?.v);
    return q;
  }

  const q = parseQuery();
  need(p === toks.length, "unexpected trailing tokens");
  return q;
}

// colN identifiers read from the base table (names defined by the query itself, e.g. "AS col1", are excluded)
function sqlRefs(q) {
  const refs = new Set(), aliases = new Set();
  walk(q, (n) => {
    if (n.k === "id") { const m = /^col(\d+)$/i.exec(n.name.split(".").pop()); if (m) refs.add(+m[1]); }
    n.items?.forEach((it) => it.alias && aliases.add(it.alias.toLowerCase()));
  });
  return [...refs].filter((n) => !aliases.has(`col${n}`));
}
function hasAgg(n) { // non-window single-arg aggregate (e.g. MAX(x)) => whole-table / per-group query
  if (!n || typeof n !== "object" || n.k === "over" || n.k === "subq") return false;
  if (n.k === "call" && isAggName(n.name) && n.args.length === 1) return true;
  return Object.values(n).some((v) => (Array.isArray(v) ? v.some(hasAgg) : hasAgg(v)));
}

// MySQL-like compare: text vs text is case-insensitive, otherwise numeric. Returns -1/0/1, or null for NULL.
function sqlCompare(a, b) {
  if (a === null || b === null || a === undefined || b === undefined) return null;
  if (typeof a === "string" && typeof b === "string") { const x = a.trimEnd().toLowerCase(), y = b.trimEnd().toLowerCase(); return x < y ? -1 : x > y ? 1 : 0; }
  const x = Number(a), y = Number(b);
  if (Number.isNaN(x) || Number.isNaN(y)) { const s = String(a), t = String(b); return s < t ? -1 : s > t ? 1 : 0; }
  return x < y ? -1 : x > y ? 1 : 0;
}
const orderCmp = (order, a, b) => { for (const o of order) { const c = sqlCompare(ev(o.e, a), ev(o.e, b)) ?? 0; if (c) return o.desc ? -c : c; } return 0; };

function callScalar(name, vals) {
  if (Object.prototype.hasOwnProperty.call(SCALAR_EXTRA, name)) return SCALAR_EXTRA[name](...vals);
  if (!Object.prototype.hasOwnProperty.call(MATH_FUNCTIONS, name)) throw new Error(`unsupported function ${name}()`);
  const nums = vals.map((v) => (v === null || v === "" ? NaN : Number(v)));
  if (nums.some(Number.isNaN)) return null;
  const r = MATH_FUNCTIONS[name](...nums);
  return Number.isFinite(r) ? r : null;
}
function aggregate(name, arg, rows, cx) {
  if (arg.k === "star") return rows.length;
  const vals = rows.map((r) => ev(arg, { ...cx, row: r })).filter((v) => v !== null && v !== undefined);
  if (name === "count") return vals.length;
  const nums = vals.map(Number).filter(Number.isFinite);
  return nums.length ? computeAggregate(name, nums) : null;
}
function lookupColumn(name, cx) {
  const key = name.split(".").pop(), row = cx.row;
  if (Object.prototype.hasOwnProperty.call(row, key)) return row[key];
  const k = Object.keys(row).find((c) => c.toLowerCase() === key.toLowerCase()); // identifiers are case-insensitive
  if (k !== undefined) return row[k];
  const ds = cx.env.datasheet; // bare datasheet field (e.g. accuracy, lc) as a fallback
  if (ds && Object.prototype.hasOwnProperty.call(ds, key)) return cellValue(ds[key]);
  throw new Error(`unknown column "${name}"`);
}

// cx = { row, rows (all rows of this query level — the window universe), grp (rows of the current group, or null), env }
function ev(n, cx) {
  const e = (x) => ev(x, cx);
  switch (n.k) {
    case "lit": return n.v;
    case "id": return lookupColumn(n.name, cx);
    case "neg": { const v = e(n.e); return v === null ? null : -v; }
    case "not": { const v = e(n.e); return v === null ? null : !truthy(v); }
    case "bin": {
      if (n.op === "&&") return truthy(e(n.l)) && truthy(e(n.r));
      if (n.op === "||") return truthy(e(n.l)) || truthy(e(n.r));
      const a = e(n.l), b = e(n.r);
      if (a === null || b === null) return null;
      const r = ARITH[n.op](Number(a), Number(b));
      return Number.isFinite(r) ? r : null;
    }
    case "cmp": { const c = sqlCompare(e(n.l), e(n.r)); return c === null ? null : CMP_FN[n.op](c); }
    case "between": {
      const v = e(n.e), lo = sqlCompare(v, e(n.a)), hi = sqlCompare(v, e(n.b));
      return lo === null || hi === null ? null : (lo >= 0 && hi <= 0) !== !!n.neg;
    }
    case "in": { const v = e(n.e); return v === null ? null : n.list.some((x) => sqlCompare(v, e(x)) === 0) !== !!n.neg; }
    case "isnull": return (e(n.e) === null) !== !!n.neg;
    case "case": {
      const subj = n.subject ? e(n.subject) : null;
      for (const b of n.branches) { const c = e(b.c); if (n.subject ? sqlCompare(subj, c) === 0 : truthy(c)) return e(b.v); }
      return n.els ? e(n.els) : null; // no ELSE -> NULL (cell stays unresolved)
    }
    case "cast": {
      const v = e(n.e);
      if (v === null) return null;
      if (/char|text/.test(n.type)) return String(v);
      const x = Number(v);
      if (Number.isNaN(x)) return null;
      if (/int|signed/.test(n.type)) return Math.trunc(x);
      return /decimal|numeric/.test(n.type) ? Number(roundWithDecimal(x, n.scale)) : x;
    }
    case "call": return isAggName(n.name) && n.args.length === 1 ? aggregate(n.name, n.args[0], cx.grp || cx.rows, cx) : callScalar(n.name, n.args.map(e));
    case "over": {
      const keyOf = (r) => n.part.map((x) => ev(x, { ...cx, row: r })).join("\u241F"), key = keyOf(cx.row), name = n.fn.name;
      const slot = cx.env.cache.get(n) || cx.env.cache.set(n, new Map()).get(n); // per-partition rows (sorted once) + whole-partition aggregates
      let part = slot.get(key);
      if (!part) {
        part = cx.rows.filter((r) => keyOf(r) === key);
        if (n.order.length) part.sort((a, b) => orderCmp(n.order, { ...cx, row: a }, { ...cx, row: b }));
        slot.set(key, part);
      }
      if (name === "row_number") return part.indexOf(cx.row) + 1;
      if (!isAggName(name)) throw new Error(`unsupported window function ${name}()`);
      if (n.order.length) return aggregate(name, n.fn.args[0], part.slice(0, part.indexOf(cx.row) + 1), cx); // ORDER BY => running frame
      const vk = `\0${key}`;
      if (!slot.has(vk)) slot.set(vk, aggregate(name, n.fn.args[0], part, cx));
      return slot.get(vk);
    }
    case "subq": { // uncorrelated scalar subquery, evaluated once
      if (!cx.env.cache.has(n)) { const r = runQuery(n.q, cx.env); cx.env.cache.set(n, r[0] ? Object.values(r[0])[0] : null); }
      return cx.env.cache.get(n);
    }
    default: throw new Error(`unsupported expression "${n.k}"`);
  }
}

function runQuery(q, env) {
  let rows = q.from.sub ? runQuery(q.from.sub, env) : env.baseRows(q.from);
  if (q.where) { const all = rows; rows = all.filter((r) => truthy(ev(q.where, { row: r, rows: all, env }))); }
  const aggregated = q.groupBy.length > 0 || q.items.some((it) => hasAgg(it.e));
  const units = !aggregated ? rows.map((r) => [r, null])
    : q.groupBy.length ? [...groupBy(rows, (r) => q.groupBy.map((g) => ev(g, { row: r, rows, env })).join("\u241F")).values()].map((g) => [g[0], g])
    : [[rows[0] ?? {}, rows]];
  let res = units.map(([row, grp]) => {
    const cx = { row, rows, grp, env }, out = {};
    q.items.forEach((it) => (it.star ? Object.assign(out, row) : (out[it.alias] = ev(it.e, cx))));
    return { out, cx: { ...cx, row: { ...row, ...out } } }; // ORDER BY sees input columns + aliases
  });
    if (q.order.length) res.sort((a, b) => orderCmp(q.order, a.cx, b.cx));
  res = res.map((r) => r.out);
  if (q.offset) res = res.slice(q.offset);
  return q.limit === null ? res : res.slice(0, q.limit);
}

const baseRowObject = (row) => { // { id, col1..colN } — colN reads c(N-1), the last+1 column reads `uncertainty`
  const o = { id: row.id };
  for (let n = 1; n <= lastCIndex(row) + 2; n++) o[`col${n}`] = cellValue(readCol(row, n));
  return o;
};

async function applySqlQueryStep(step, ctx) {
  const { rowsByTable, dataByTable, configByTable, tableIdByIndex, datasheet, stepResults, standardRanges } = ctx;
  const { targetTableId, targetCol, stepNo, scope } = step;
  const targetRows = step.isPassthrough ? null : rowsByTable[targetTableId];
  if (!step.isPassthrough && !targetRows) return skip(ctx, step, `target table ${targetTableId} has no rows`);

  const resolved = {}; // $datasheet.x / $stepN / $standardRanges.x -> literal text
  for (const t of new Set(step.sql.match(SQL_PLACEHOLDER_RE) || [])) {
    const { expr, missing } = await resolveStepPlaceholders(t, datasheet, stepResults, standardRanges);
    if (missing.length) return skip(ctx, step, `waiting for ${missing.join(", ")}`);
    resolved[t] = expr;
  }

  let q, result;
  try {
    const sql = step.sql.replace(SQL_PLACEHOLDER_RE, (t) => resolved[t]);
    if (step.cachedSql !== sql) { step.ast = parseSql(sql); step.refs = sqlRefs(step.ast); step.cachedSql = sql; } // re-parse only when placeholder values change
    q = step.ast;
    const baseRows = ({ id, index }) => {
      const tid = id ?? tableIdByIndex[index - 1];
      if (tid === undefined) throw new Error(`no table config with index ${index - 1} for "t${index}" (config indexes found: ${Object.keys(tableIdByIndex).join(",") || "none"})`);
      const rows = dataByTable[tid];
      if (!rows?.length) throw new NotReady(`source table ${tid} has no rows`);
      const bad = step.refs.filter((n) => n <= lastCIndex(rows[0]) + 2 && rows.some((r) => !hasValue(readCol(r, n))));
      if (bad.length) throw new NotReady(`table ${tid}: ${bad.map((n) => `col${n} (reads ${colSource(rows[0], n)})`).join(", ")} not resolved yet`);
      return rows.map(baseRowObject);
    };
    result = runQuery(q, { datasheet, cache: new Map(), baseRows });
  } catch (e) {
    if (!(e instanceof NotReady) && !ctx.logged.has(stepNo)) { ctx.logged.add(stepNo); console.error(`Step ${stepNo} SQL error: ${e.message}`); }
    return skip(ctx, step, e.message);
  }

  const key = q.items.find((it) => !it.star)?.alias; // the first selected column is the one written out
  if (key === undefined) return skip(ctx, step, "query selects no column");
  const values = result.map((r) => (typeof r[key] === "boolean" ? +r[key] : r[key])), first = values[0];

  if (step.isPassthrough) return Number.isFinite(first) ? commitPassthroughResult(step, first, ctx) : skip(ctx, step, "query returned no numeric value");

  // one-row result from an aggregate query or LIMIT 1 => broadcast (like the old LIMIT/aggregate steps); else positional per-row
  const single = result.length === 1 && (q.limit === 1 || (!q.groupBy.length && q.items.some((it) => hasAgg(it.e))));
  if (single) return first === null || first === undefined ? skip(ctx, step, "query returned NULL") : commitStepResult(step, first, targetRows, ctx);

  const dataRows = dataByTable[targetTableId], raw = [], fin = [], write = rowWriter(targetCol, configByTable[targetTableId], ctx, raw, fin);
  let changed = false;
  if (scope === "all") {
    dataRows.forEach((row, i) => { changed = write(row, values[i]) || changed; });
    if (values.length !== dataRows.length) ctx.skipReasons[stepNo] = `query returned ${values.length} row(s) but target table ${targetTableId} has ${dataRows.length} data row(s) — ${values.length > dataRows.length ? "extra row(s) dropped" : "trailing row(s) left unset"}`;
  } else if (dataRows[scope - 1]) changed = write(dataRows[scope - 1], values[scope - 1]);
  else ctx.skipReasons[stepNo] = `scope "row${scope}" is out of range — only ${dataRows.length} data row(s) found`;
  return record(ctx, step, raw, fin, changed);
}

/* ------------------------------ step parsing ------------------------------ */

// Fixed shapes (fast paths):
//   select avg(col6) as col1 from tid10 | select round(...(select max(col6) from tid40)...) as col1
//   select greatest(abs(col3),abs(col4)) as col1 from t1 where col2=0 limit 1 | select ($datasheet.a + $datasheet.b)/2 as col1 from t1
//   select case when col2 = 0 then ... else 0 end as col1 from t1 limit 1 | select ... ($step1 ...) as col1 from t1
//   select col8 as col1 from t2 (no LIMIT + ".all" -> per-row projection)
//   select avg(col4) as col1 from t1 group by col2[, col3...] order by null
//   SELECT s.col4 AS col1 FROM t1 JOIN (SELECT col2[, unit], AGG(col4) AS col4 FROM t1 GROUP BY col2[, unit] order by null) s ON t1.col2 = s.col2 [AND ...]
//   SELECT colP AS col1 FROM (SELECT colG,colP,id,ROW_NUMBER() OVER (PARTITION BY colG ORDER BY id) AS rn FROM t1) AS alias WHERE (rn-1)%M=K ORDER BY MIN(id) OVER (PARTITION BY colG), rn
// Anything else (or anything with OVER/CAST/'...'/FROM (...)) -> the general SQL engine above.
// Output stream: "tid<N>.col<M>" or "tid<N>.col<M>.<all|rowN>" (scope optional, default "all"); no "tid" = passthrough.
const from = (idPrefix, n) => (idPrefix ? { sourceTableId: +n, sourceTableIndex: null } : { sourceTableId: null, sourceTableIndex: +n });
const csv = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

function parseStep(step, stepNo) { // SYNC — used in a .map().filter(Boolean) chain
  if (!step?.instructions?.length || !step?.outputStream) return null;
  const instruction = step.instructions[0].trim().replace(/;+\s*$/, "").replace(/\s+/g, " ");
  const outRaw = String(step.outputStream).trim();
  const outMatch = outRaw.match(/^tid(\d+)\.col(\d+)(?:\.(.+))?$/i), isPassthrough = !outMatch;
  if (isPassthrough && /\btid\d+/i.test(outRaw)) { console.error("Unable to parse step outputStream:", step.outputStream); return null; }

  const m = (re) => instruction.match(re);
  const simpleMatch = m(/^select\s+(\w+)\(col(\d+)\)\s+as\s+col\d+\s+from\s+tid(\d+)\s*$/i);
  const genericMatch = m(/^select\s+([\s\S]+?)\s+as\s+col\d+(?:\s+from\s+t(id)?(\d+))?(?:\s+where\s+col(\d+)\s*=\s*(-?\d+(?:\.\d+)?))?(?:\s+limit\s+(\d+))?\s*$/i);
  const groupBySimpleMatch = m(/^select\s+(\w+)\s*\(\s*col(\d+)\s*\)\s+as\s+col\d+\s+from\s+t(id)?(\d+)\s+group\s+by\s+([\w\s,]+?)\s*(?:order\s+by\s+null)?\s*$/i);
  const groupByJoinMatch = m(/^select\s+\w+\.\w+\s+as\s+col\d+\s+from\s+t(id)?(\d+)(?:\s+as\s+\w+)?\s+join\s*\(\s*([\s\S]+?)\s*\)\s+(?:as\s+)?\w+\s+on\s+[\s\S]+?\s*$/i);
  const groupByJoinInner = groupByJoinMatch?.[3].match(/^select\s+([\w\s,]+?)\s*,\s*(\w+)\s*\(\s*col(\d+)\s*\)\s+as\s+\w+\s+from\s+t(id)?(\d+)\s+group\s+by\s+([\w\s,]+?)\s*(?:order\s+by\s+null)?\s*$/i);
  const windowedModuloMatch = m(/^select\s+(?:\w+\.)?col(\d+)\s+as\s+col\d+\s+from\s*\(\s*select\s+[\s\S]+?row_number\(\)\s*over\s*\(\s*partition\s+by\s+col(\d+)\s+order\s+by\s+id\s*\)\s+as\s+rn\s+from\s+t(id)?(\d+)\s*\)\s+as\s+\w+\s+where\s+\(\s*rn\s*-\s*1\s*\)\s*%\s*(\d+)\s*=\s*(\d+)\s+order\s+by\s+min\(id\)\s+over\s*\(\s*partition\s+by\s+col\d+\s*\)\s*,\s*rn\s*$/i);
  // normalized to [agg, aggCol, idPrefix, fromNum, groupByList]
  const grouped = groupBySimpleMatch ? groupBySimpleMatch.slice(1) : groupByJoinInner ? [...groupByJoinInner.slice(2, 6), groupByJoinInner[1]] : null;

  let expression, mode = "aggregate", src = { sourceTableId: null, sourceTableIndex: null };
  const f = { whereCol: null, whereVal: null, limit: null, aggFunc: null, aggColNum: null, groupByParts: null, groupByColNum: null, modulus: null, remainder: null };

  if (simpleMatch) expression = `(SELECT ${simpleMatch[1]}(col${simpleMatch[2]}) FROM tid${simpleMatch[3]})`;
  else if (windowedModuloMatch) {
    const [, sel, grp, p, n, mod, rem] = windowedModuloMatch;
    mode = "windowedModuloProjection";
    Object.assign(f, { aggColNum: +sel, groupByColNum: +grp, modulus: +mod, remainder: +rem });
    src = from(p, n);
  } else if (grouped) {
    const [agg, aggCol, p, n, list] = grouped;
    mode = "groupedProjection";
    Object.assign(f, { aggFunc: agg.toLowerCase(), aggColNum: +aggCol, groupByParts: csv(list) });
    src = from(p, n);
  } else if (!genericMatch || SQL_ONLY_RE.test(instruction)) {
    try { parseSql(instruction); mode = "sqlQuery"; } // validated here; re-parsed at run time once $placeholders are filled in
    catch (e) { console.error("Unable to parse step instruction:", instruction, "—", e.message); return null; }
  } else {
    const [, expr, p, n, wCol, wVal, lim] = genericMatch;
    expression = expr;
    if (n) src = from(p, n);
    if (wCol) f.whereCol = +wCol;
    if (wVal !== undefined) f.whereVal = parseFloat(wVal);
    if (lim) f.limit = +lim;
    if (!/\(\s*select\b/i.test(expression) && (src.sourceTableId !== null || src.sourceTableIndex !== null)) mode = "rowFilter";
  }

  let deps = [], depColNums = null;
  if (mode === "rowFilter") { // ignore $datasheet.x so it's never mistaken for a colN
    depColNums = collectColRefs(expression.replace(DATASHEET_REF_RE, ""));
    if (f.whereCol !== null) depColNums.push(f.whereCol);
  } else if (mode === "aggregate") deps = collectSubqueryDeps(expression);

  let scope = null, targetTableId = null, targetCol = null;
  if (!isPassthrough) { // 3rd segment optional: "all"/"rowall"/"row<N>" (any case/whitespace), default "all"
    const [, tid, colNum, rawScope] = outMatch;
    targetTableId = +tid;
    targetCol = `c${+colNum}`;
    const s = (rawScope ?? "all").trim().toLowerCase();
    if (s.includes("row")) { const r = s.split("row")[1].trim(); scope = r === "all" ? "all" : Number(r); }
    else scope = s === "all" || s === "" ? "all" : s;
    if (scope !== "all" && !Number.isInteger(scope)) console.warn(`Unrecognized outputStream scope "${rawScope}" (expected "all"/"rowall" or "row<N>"):`, step.outputStream);
  }
  return { stepNo, query: step.instructions[0].trim(), mode, isPassthrough, expression, sql: mode === "sqlQuery" ? instruction : null, ...src, ...f, deps, depColNums, targetTableId, targetCol, scope };
}

/* -------------------- aggregate-mode expression resolver -------------------- */

const SUBQUERY_RE = /\(\s*select\s+(\w+)\s*\(\s*col(\d+)\s*\)\s*from\s+tid(\d+)\s*\)/gi;
const collectSubqueryDeps = (expr) => [...expr.matchAll(SUBQUERY_RE)].map((m) => ({ colNum: +m[2], tableId: +m[3] })); // SYNC

function computeSubqueryValue(aggFunc, colNum, tableId, dataByTable) { // SYNC — used directly in a String.replace callback
  const rows = dataByTable[tableId];
  if (!rows?.length) return null;
  const values = rows.map((r) => stepNumVal(readCol(r, colNum)));
  return values.some(isNaN) ? null : computeAggregate(aggFunc.toLowerCase(), values);
}
function findMatchingParen(str, openIdx) { // SYNC
  let depth = 0;
  for (let i = openIdx; i < str.length; i++) {
    if (str[i] === "(") depth++;
    else if (str[i] === ")" && --depth === 0) return i;
  }
  return -1;
}
function splitTopLevelArgs(args) { // SYNC — splits on the first top-level comma
  let depth = 0;
  for (let i = 0; i < args.length; i++) {
    if (args[i] === "(") depth++;
    else if (args[i] === ")") depth--;
    else if (args[i] === "," && depth === 0) return { valueExpr: args.slice(0, i), precisionExpr: args.slice(i + 1) };
  }
  return { valueExpr: args, precisionExpr: null };
}

// ROUND(expr, n), nested first; precision is the inline one, not resolveDynamicPrecision.
async function resolveRoundCalls(expr) {
  const roundRe = /round\s*\(/i;
  let match;
  while ((match = roundRe.exec(expr))) {
    const start = match.index, openIdx = start + match[0].length - 1, closeIdx = findMatchingParen(expr, openIdx);
    if (closeIdx === -1) { console.error("Unbalanced parentheses in ROUND():", expr); return null; }
    const { valueExpr, precisionExpr } = splitTopLevelArgs(expr.slice(openIdx + 1, closeIdx));
    const resolved = await resolveRoundCalls(valueExpr);
    if (resolved === null) return null;
    const value = await safeEvalArithmetic(resolved);
    if (value === null) return null;
    expr = `${expr.slice(0, start)}(${Number(roundWithDecimal(value, precisionExpr ? parseInt(precisionExpr.trim(), 10) : 0))})${expr.slice(closeIdx + 1)}`;
  }
  return expr;
}

// Innermost "(select <expr> [as colN] from t|tid N)" — <expr> may combine several aggregates.
const GENERAL_SUBQUERY_RE = /\(\s*select\s+((?:(?!\bselect\b)[\s\S])+?)(?:\s+as\s+col\d+)?\s+from\s+t(id)?(\d+)\s*\)/i;
async function resolveGeneralSubqueries(expr, ctx) {
  let out = expr, match;
  while ((match = GENERAL_SUBQUERY_RE.exec(out))) {
    const [full, inner, idPrefix, numStr] = match, num = +numStr;
    const tableId = idPrefix ? num : ctx.tableIdByIndex[num - 1];
    if (tableId === undefined) return { expr: null, reason: `no table config with index ${num - 1} for "t${num}"` };
    const rows = ctx.dataByTable[tableId];
    if (!rows?.length) return { expr: null, reason: `source table ${tableId} has no rows` };
    const { expr: aggResolved, missing } = await resolveColumnAggregateCalls(inner, rows);
    if (missing.length) return { expr: null, reason: `table ${tableId}: ${missing.join(", ")}` };
    if (collectColRefs(aggResolved).length) return { expr: null, reason: `subquery on table ${tableId} uses colN outside an aggregate: ${inner}` };
    const value = await safeEvalArithmetic(aggResolved);
    if (value === null) return { expr: null, reason: `subquery could not be evaluated: ${inner}` };
    out = out.slice(0, match.index) + `(${toPlain(value)})` + out.slice(match.index + full.length);
  }
  return { expr: out, reason: null };
}

// subqueries -> ROUND() -> plain arithmetic
async function resolveExpression(rawExpr, ctx) {
  ctx.lastExpressionReason = null;
  let unresolved = false;
  let expr = rawExpr.replace(SUBQUERY_RE, (_, agg, colNum, tableId) => { // computeSubqueryValue must stay sync
    const value = computeSubqueryValue(agg, +colNum, +tableId, ctx.dataByTable);
    if (value === null) { unresolved = true; return "0"; }
    return `(${toPlain(value)})`;
  });
  if (unresolved) return null;
  const general = await resolveGeneralSubqueries(expr, ctx); // multi-aggregate / positional "tN" forms
  if (general.expr === null) { ctx.lastExpressionReason = general.reason; return null; }
  expr = await resolveRoundCalls(general.expr);
  return expr === null ? null : safeEvalArithmetic(expr);
}

/* ----------------- aggregate functions (SYNC throughout) ----------------- */

const sum = (a) => a.reduce((x, y) => x + y, 0);
const mean = (a) => sum(a) / a.length;
const sumSquares = (a, center = 0) => a.reduce((acc, v) => acc + (v - center) ** 2, 0);
const popVariance = (a) => sumSquares(a, mean(a)) / a.length;
const sampleVariance = (a) => (a.length < 2 ? 0 : sumSquares(a, mean(a)) / (a.length - 1));
const product = (a) => a.reduce((x, y) => x * y, 1);
const popStd = (a) => Math.sqrt(popVariance(a));
const sampleStd = (a) => Math.sqrt(sampleVariance(a));

const AGGREGATES = {
  avg: mean, max: (a) => Math.max(...a), min: (a) => Math.min(...a), sum, sq: (a) => sumSquares(a),
  // std/stddev_pop = POPULATION (÷N); stddev_samp = SQL sample std (÷N-1) — the old engine ran step queries as literal SQL.
  std: popStd, stddev_samp: sampleStd, stddev_pop: popStd,
  variance: sampleVariance, var_samp: sampleVariance, var_pop: popVariance,
  median: (a) => { const s = [...a].sort((x, y) => x - y), mid = Math.floor(s.length / 2); return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2; },
  count: (a) => a.length, product, prod: product,
  range: (a) => Math.max(...a) - Math.min(...a),
  abs_sum: (a) => a.reduce((acc, v) => acc + Math.abs(v), 0),
  rms: (a) => Math.sqrt(sumSquares(a) / a.length),
};

function computeAggregate(aggFunc, values) {
  if (!values.length) return null;
  if (!Object.prototype.hasOwnProperty.call(AGGREGATES, aggFunc)) { console.error("Unsupported aggregate function:", aggFunc); return null; }
  return AGGREGATES[aggFunc](values);
}